import '../tables.css';

import { useMemo } from 'react';

import { useRecoilValue } from 'recoil';
import { userConfigState } from '../../../recoil/atoms/userConfigState';
import { useAuthContext } from '../../../../common/contexts/useAuthContext';
import { tenantSettingsState } from '../../../recoil/atoms/tenantSettingsState';
import { tenantScopesState } from '../../../recoil/atoms/tenantScopesState';
import { userSettingsState } from '../../../recoil/atoms/userSettingsState';
import { useSetSelectedPlayer } from '../../../recoil/hooks/useSetSelectedPlayer';
import { useOpenGlobalModal } from '../../../recoil/hooks/useOpenGlobalModal';

import PermScanWifiRoundedIcon from '@mui/icons-material/PermScanWifiRounded';

import {
  alertTableInfoColumns,
  alertTableOverallIndexColumnWidth,
  alertTablePadding,
  alertTableRoleIndexColumnWidth,
  alertTableStaticColumnsWidth,
  getAlertTableActionColumn,
  getAlertTableOverallDataColumns,
  getAlertTableRoleIndexColumns,
} from './alertColumns';

import { PlayerOverview } from '../../../types/player';
import { useWindowSize } from '../../../../common/hooks/WindowSize';
import { alertTablePageSize } from '../../../views/scouting/Scouting';
import { getTenantSecondaryColor } from '../../../static/tenantConfigs';
import { translate } from '../../../../common/language/translations';
import { PlayerInfoCell, PlayerPaginationCell } from '../tableCells';
import { getDisplayPositions } from '../../../utils/positionUtils';
import { getNavigationWidth } from '../../../utils/navigationUtils';
import { IconButton } from '../../controls/buttons/IconButton';
import { getRoleConfigTableFontSize } from '../../../utils/fontSizeUtils';
import { Toggle } from '../../controls/input/Toggle';
import { updateSeasonStatsToggles } from '../../../services/firestore/userSettings';
import { playerMetricMap } from '../../../static/playerMetrics';
import { isLimitedRoleConfig } from '../../../utils/metricUtils';
import { positionGroupToPositions } from '../../../static/constants/positions';


interface AlertTableProps {
  data: PlayerOverview[];

  isExcludedPlayersView: boolean;
  isShowAllExcludedPlayersToggled: boolean;

  excludePlayer: (playerId: number, allAlerts: boolean, monthsExcluded: number) => void,
  undoExcludePlayer: (playerOverview: PlayerOverview) => void,
  excludeAllPlayers: () => void,
  undoAllExcludedPlayers: () => void,

  currentPage: number;
  totalHits: number;
  handleChangeCurrentPage: (increment: boolean) => void;

  selectedOrderBy: string | undefined;
  selectedRoles: string[];
}


export const AlertTable: React.FC<AlertTableProps> = ({
  data,

  isExcludedPlayersView,
  isShowAllExcludedPlayersToggled,

  excludePlayer,
  undoExcludePlayer,
  excludeAllPlayers,
  undoAllExcludedPlayers,

  currentPage,
  totalHits,
  handleChangeCurrentPage,

  selectedOrderBy,
  selectedRoles,
}) => {

  const { currentUser } = useAuthContext();

  const userConfig = useRecoilValue(userConfigState);
  const userSettings = useRecoilValue(userSettingsState);
  const tenantSettings = useRecoilValue(tenantSettingsState);
  const tenantScopes = useRecoilValue(tenantScopesState);

  const { setAndTrackSelectedPlayer } = useSetSelectedPlayer();
  const { openTextModal, openRoleInfoModal, openConfirmModal } = useOpenGlobalModal();
  const { width } = useWindowSize();

  const roleConfigs = useMemo(() => tenantSettings?.roleConfigs ?? {}, [tenantSettings?.roleConfigs]);

  const positionGroupOfSelectedRoles = useMemo(() => {
    if (selectedRoles && selectedRoles.length > 0 && roleConfigs[selectedRoles[0]]) {
      return roleConfigs[selectedRoles[0]].positionGroup;
    }
  }, [selectedRoles, roleConfigs]);


  const overallDataColumns = useMemo(() => {
    return getAlertTableOverallDataColumns(
      tenantScopes?.hasSkillcorner ?? false,
      userSettings?.seasonStatsToggles?.showOverallIndexConfidences ?? false
    );
  }, [tenantScopes?.hasSkillcorner, userSettings?.seasonStatsToggles?.showOverallIndexConfidences]);


  const roleIndexColumns = useMemo(() => {
    return getAlertTableRoleIndexColumns(
      selectedRoles,
      userSettings?.seasonStatsToggles?.showRoleIndexConfidences ?? false
    );
  }, [selectedRoles, userSettings?.seasonStatsToggles?.showRoleIndexConfidences]);


  const actionColumn = useMemo(() => {
    return getAlertTableActionColumn(isExcludedPlayersView);
  }, [isExcludedPlayersView]);


  const handleRowClick = (playerOverview: PlayerOverview) => {
    const initialPositionGroup = selectedRoles && selectedRoles.length > 0 && roleConfigs[selectedRoles[0]]
      ? roleConfigs[selectedRoles[0]].positionGroup
      : undefined;

    setAndTrackSelectedPlayer({
      id: playerOverview.id,
      name: playerOverview.name,
      playerOverview: playerOverview,
      selectedPositionGroup: initialPositionGroup,
    });
  };


  const tableWidth = width - 24 - getNavigationWidth(userConfig);
  const alertTableStickyColumnWidth = 290;
  const alertTableNonStickyWidth = tableWidth - alertTableStickyColumnWidth;

  const alertTableNonStickyMinWidth = alertTableStaticColumnsWidth
    + (tenantScopes?.hasSkillcorner ? alertTableOverallIndexColumnWidth : 0)
    + (selectedRoles.length * alertTableRoleIndexColumnWidth);

  const widthUnit = Math.max(alertTableNonStickyWidth / alertTableNonStickyMinWidth, 1);


  return (
    <div
      className='table-container player-table-container'
      style={{ overflowX: widthUnit <= 1 ? 'auto' : 'hidden', fontSize: 12 }}
    >

      {/* Header */}
      <div className='table-header player-table-header player-table-header-background'>

        {/* Player column header */}
        <div
          className='table-sticky-left player-table-header-background player-table-header-cell-with-border'
          style={{ width: alertTableStickyColumnWidth }}
        >
          <PlayerPaginationCell
            totalHits={totalHits}
            currentChapter={currentPage - 1}
            hitsPerChapter={alertTablePageSize}
            handleChangeCurrentChapter={handleChangeCurrentPage}
            language={userConfig?.language}
          />
        </div>


        {/* Info column headers */}
        <div className='table-header-group player-table-header-cell-with-border'>
          <div className='table-header-group-top-row'>
            {translate('info', userConfig?.language)}
          </div>

          <div className='table-header-group-bottom-row'>
            {alertTableInfoColumns.map(column => {

              const isOrderedBy = selectedOrderBy && selectedOrderBy === column.orderByKey;

              return (
                <div
                  key={column.key}
                  className='table-cell'
                  style={{
                    width: column.width * widthUnit,
                    color: isOrderedBy ? getTenantSecondaryColor(userConfig?.club ?? '') : undefined,
                    fontWeight: isOrderedBy ? 600 : undefined,
                  }}
                >
                  {translate(column.key, userConfig?.language)}
                </div>
              );
            })}
          </div>
        </div>


        {/* Overall data column headers */}
        <div className='table-header-group player-table-header-cell-with-border'>

          <div className='table-header-group-top-row'>
            <div
              className='table-header-cell-clickable'
              style={{ padding: 0 }}
              onClick={() => openTextModal(
                translate('dataBasis', userConfig?.language),
                translate('dataBasisInfo', userConfig?.language),
                { infoType: 'infoIcon', titleKey: 'dataBasis' },
              )}
            >
              {translate('allPositions', userConfig?.language)}
            </div>

            {userConfig && currentUser && (
              <div
                className='table-header-toggle'
                title={translate('showConfidence', userConfig?.language)}
              >
                <Toggle
                  isToggled={userSettings?.seasonStatsToggles?.showOverallIndexConfidences ?? false}
                  setIsToggled={(value: boolean) => updateSeasonStatsToggles(
                    { showOverallIndexConfidences: value },
                    userConfig.email,
                    userConfig.club,
                    currentUser
                  )}
                  isSmall={true}
                />
              </div>
            )}
          </div>

          <div className='table-header-group-bottom-row'>
            {overallDataColumns.map(column => {

              const isOrderedBy = selectedOrderBy && selectedOrderBy === column.orderByKey;

              const metricTitle = playerMetricMap[column.key]?.name[userConfig?.language ?? 'en']
                ?? translate(column.key, userConfig?.language);

              const metricDescription = playerMetricMap[column.key]?.description[userConfig?.language ?? 'en']
                ?? translate(column.key + 'Info', userConfig?.language);

              return (
                <div
                  key={column.key}
                  className='table-cell'
                  style={{ width: column.width * widthUnit }}
                >
                  <div className='flex-row align-center' style={{ position: 'relative' }}>
                    <div
                      className='table-header-cell-clickable'
                      style={{
                        color: isOrderedBy ? getTenantSecondaryColor(userConfig?.club ?? '') : undefined,
                        fontWeight: isOrderedBy ? 600 : undefined,
                        textWrap: 'nowrap'
                      }}
                      onClick={() => openTextModal(metricTitle, metricDescription, { infoType: 'metric', titleKey: column.key })}
                      title={translate('showDescription', userConfig?.language)}
                    >
                      {metricTitle}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Role index column headers */}
        {positionGroupOfSelectedRoles && (
          <div className='table-header-group player-table-header-cell-with-border'>

            <div
              className='table-header-group-top-row'
              style={{ fontSize: positionGroupToPositions[positionGroupOfSelectedRoles].length > 2 ? 11 : 12 }}
            >
              <div
                className='table-header-cell-clickable'
                style={{ padding: 0 }}
                onClick={() => openTextModal(
                  translate('dataBasis', userConfig?.language),
                  translate('dataBasisInfo', userConfig?.language),
                  { infoType: 'infoIcon', titleKey: 'dataBasis' },
                )}
              >
                {getDisplayPositions(
                  positionGroupToPositions[positionGroupOfSelectedRoles],
                  userConfig?.language,
                )}
              </div>

              {userConfig && currentUser && (
                <div
                  className='table-header-toggle'
                  title={translate('showConfidence', userConfig?.language)}
                >
                  <Toggle
                    isToggled={userSettings?.seasonStatsToggles?.showRoleIndexConfidences ?? false}
                    setIsToggled={(value: boolean) => updateSeasonStatsToggles(
                      { showRoleIndexConfidences: value },
                      userConfig.email,
                      userConfig.club,
                      currentUser
                    )}
                    isSmall={true}
                  />
                </div>
              )}
            </div>


            <div className='table-header-group-bottom-row'>
              {roleIndexColumns.map(column => {

                const roleId = column.roleId;
                const roleConfig = roleId ? roleConfigs[roleId] : undefined;
                if (!roleConfig || !roleId) return null;

                const isLimited = isLimitedRoleConfig(roleConfig);
                const isOrderedBy = selectedOrderBy && selectedOrderBy === column.orderByKey;

                return (
                  <div
                    key={column.key}
                    className='table-cell'
                    style={{ width: column.width * widthUnit }}
                  >
                    <div className='flex-row align-center' style={{ position: 'relative' }}>
                      {isLimited && (
                        <IconButton
                          onClick={() => openTextModal(
                            translate('limitedRoleConfig', userConfig?.language),
                            translate('limitedRoleConfigInfo', userConfig?.language),
                            { infoType: 'infoIcon', titleKey: '' },
                            true,
                          )}
                          icon={<PermScanWifiRoundedIcon style={{ fontSize: 11, marginBottom: -1 }} />}
                          size={14}
                          style={{ marginRight: -3 }}
                        />
                      )}

                      <div
                        className='table-header-cell-clickable'
                        style={{
                          color: isOrderedBy ? getTenantSecondaryColor(userConfig?.club ?? '') : undefined,
                          fontWeight: isOrderedBy ? 600 : undefined,
                          fontSize: getRoleConfigTableFontSize(roleConfig),
                          textWrap: 'nowrap',
                        }}
                        onClick={() => openRoleInfoModal(roleConfig)}
                        title={translate('showDescription', userConfig?.language)}
                      >
                        {roleConfigs[roleId].name}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Action column header */}
        <div className='table-header-group'>
          <div className='table-header-group-top-row' style={{ paddingRight: alertTablePadding }}>
            {translate(isExcludedPlayersView ? 'undo' : 'removeFromSearch', userConfig?.language)}
          </div>

          <div className='table-header-group-bottom-row'>
            <div
              key={actionColumn.key}
              className='table-cell'
              style={{ width: actionColumn.width * widthUnit }}
            >
              <div
                className='table-header-cell-clickable'
                style={{ paddingRight: alertTablePadding }}
                onClick={() => openConfirmModal(
                  isExcludedPlayersView ? undoAllExcludedPlayers : excludeAllPlayers,
                  translate(
                    isExcludedPlayersView ? (isShowAllExcludedPlayersToggled ? 'undoAllFromAllSearches' : 'undoAllFromThisSearch') : 'removeAll',
                    userConfig?.language
                  ),
                  translate(
                    isExcludedPlayersView ? (isShowAllExcludedPlayersToggled ? 'undoAllFromAllSearchesInfo' : 'undoAllFromThisSearchInfo') : 'removeAllInfo',
                    userConfig?.language
                  ),
                )}
              >
                {translate(actionColumn.key, userConfig?.language)}?
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Body */}
      <div>
        {data.map((row, rowIndex) => {
          return (
            <div
              key={rowIndex}
              className={
                'table-row player-table-row'
                + (rowIndex % 2 === 0 ? ' player-table-row-even' : ' player-table-row-odd')
                + (rowIndex === data.length - 1 ? ' player-table-row-last' : '')
              }
              onClick={() => handleRowClick(row)}
            >

              {/* Player column */}
              <div
                className={
                  'table-sticky-left player-table-sticky-cell player-table-body-cell-with-border'
                  + (rowIndex % 2 === 0 ? ' player-table-sticky-cell-even' : ' player-table-sticky-cell-odd')
                }
                style={{ width: alertTableStickyColumnWidth }}
              >
                <PlayerInfoCell
                  playerOverview={row}
                  language={userConfig?.language}
                  maxPlayerNameLength={18}
                  countryFlagWidth={15}
                />
              </div>

              {/* Info columns */}
              <div className='flex-row player-table-body-cell-with-border'>
                {alertTableInfoColumns.map(column => {
                  return (
                    <div
                      key={column.key}
                      className='table-cell'
                      style={{ width: column.width * widthUnit }}
                    >
                      {column.cell({ row, language: userConfig?.language })}
                    </div>
                  );
                })}
              </div>

              {/* Overall data columns */}
              <div className='flex-row player-table-body-cell-with-border'>
                {overallDataColumns.map(column => {
                  return (
                    <div
                      key={column.key}
                      className='table-cell'
                      style={{ width: column.width * widthUnit }}
                    >
                      {column.cell({ row })}
                    </div>
                  );
                })}
              </div>


              {/* Role index columns */}
              {positionGroupOfSelectedRoles && (
                <div className='flex-row player-table-body-cell-with-border'>
                  {roleIndexColumns.map(column => {
                    return (
                      <div
                        key={column.key}
                        className='table-cell'
                        style={{ width: column.width * widthUnit }}
                      >
                        {column.cell({ row, positionGroup: positionGroupOfSelectedRoles })}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Action column */}
              <div
                key={actionColumn.key}
                className='table-cell'
                style={{ width: actionColumn.width * widthUnit }}
              >
                {actionColumn.cell({ row, language: userConfig?.language, excludePlayer, isShowAllExcludedPlayersToggled, undoExcludePlayer })}
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
};
